import React, { useState } from 'react';
import { observer } from 'mobx-react';
import pricingStore from '../../Stores/PricingStore';
import './PricingCards.scss';
import { COUPON_CONFIGS, PARTNER_CONFIGS } from '../../constants/partners';

const PricingCards = observer(({ 
  onPlanSelect, 
  selectedPlan = '',
  showPaymentElement = false,
  estimateAudioDuration = (chars) => Math.round((chars / 10000) * 10.5),
  renderCta = null,
  autoAppliedCoupon = null
}) => {
  const [isYearly, setIsYearly] = useState(false);

  // Helper function to get coupon display text and pricing adjustments
  const getCouponDisplayInfo = (couponCode) => {
    if (!couponCode) return null;
    
    // Find the coupon config
    const couponConfig = Object.values(COUPON_CONFIGS).find(config => config.couponCode === couponCode) ||
                        Object.values(PARTNER_CONFIGS).find(config => config.couponCode === couponCode);
    
    if (!couponConfig) return null;
    
    const { couponDetails } = couponConfig;
    
    if (couponDetails.percent_off) {
      // Percentage discount
      return {
        type: 'percentage',
        percentOff: couponDetails.percent_off,
        duration: couponDetails.duration,
        durationInMonths: couponDetails.duration_in_months,
        displayText: `${couponDetails.percent_off}% off`,
        description: couponDetails.description
      };
    } else if (couponDetails.amount_off) {
      // Fixed amount discount
      return {
        type: 'amount',
        amountOff: couponDetails.amount_off,
        currency: couponDetails.currency,
        duration: couponDetails.duration,
        durationInMonths: couponDetails.duration_in_months,
        displayText: `${couponDetails.currency.toUpperCase()} ${(couponDetails.amount_off / 100).toFixed(2)} off`,
        description: couponDetails.description
      };
    }
    
    return null;
  };

  // Helper function to calculate discounted prices
  const getDiscountedPrice = (originalPrice, couponInfo, isYearly = false) => {
    if (!couponInfo) return originalPrice;
    
    if (couponInfo.type === 'percentage') {
      const discount = originalPrice * (couponInfo.percentOff / 100);
      return originalPrice - discount;
    }
    
    return originalPrice;
  };

  const renderToggle = () => (
    <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', margin: '20px 0' }}>
      <div 
        onClick={() => setIsYearly(false)}
        style={{
          padding: '8px 20px',
          border: '2px solid hsla(0,0%,100%,.1)',
          borderRadius: '20px',
          cursor: 'pointer',
          backgroundColor: !isYearly ? 'rgba(255,255,255,0.1)' : 'transparent',
          transition: 'background-color 0.2s ease'
        }}
      >
        Monthly
      </div>

      <div
        onClick={() => setIsYearly(true)}
        style={{
          padding: '8px 20px', 
          border: '2px solid hsla(0,0%,100%,.1)',
          borderRadius: '20px',
          cursor: 'pointer',
          backgroundColor: isYearly ? 'rgba(255,255,255,0.1)' : 'transparent',
          transition: 'background-color 0.2s ease'
        }}
      >
        Yearly <span style={{ color: '#4CAF50' }}>(Save more than 40%)</span>
      </div>
    </div>
  );

  const renderProOnlyCards = () => (
    <>
      {/* Monthly Pro Plan Card */}
      <div
        onClick={() => onPlanSelect(pricingStore.proMonthlySlug)}
        style={{
          padding: '20px',
          border: '2px solid hsla(0,0%,100%,.1)',
          borderRadius: '8px',
          width: '100%',
          maxWidth: '300px',
          height: '500px',
          cursor: showPaymentElement ? 'not-allowed' : 'pointer',
          backgroundColor: selectedPlan === pricingStore.proMonthlySlug ? 'rgba(255,255,255,0.1)' : 'transparent',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative'
        }}
      >
        {autoAppliedCoupon && (
          <div style={{ 
            position: 'absolute',
            top: '-12px',
            left: '20px',
            backgroundColor: '#4ade80',
            color: 'white',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 'bold'
          }}>
            SPECIAL OFFER
          </div>
        )}
        <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>Monthly Pro</div>
        <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'rgb(255 143 65)' }}>
          {(() => {
            const couponInfo = getCouponDisplayInfo(autoAppliedCoupon);
            if (!couponInfo) return `$${pricingStore.proMonthlyPrice}`;
            
            if (couponInfo.type === 'percentage') {
              const discountedPrice = getDiscountedPrice(pricingStore.proMonthlyPrice, couponInfo);
              return (
                <>
                  <span style={{ textDecoration: 'line-through', opacity: 0.6, fontSize: '24px' }}>${pricingStore.proMonthlyPrice}</span>
                  <span style={{ color: '#4ade80', marginLeft: '8px' }}>${discountedPrice.toFixed(2)}</span>
                </>
              );
            } else {
              return (
                <>
                  <span style={{ textDecoration: 'line-through', opacity: 0.6, fontSize: '24px' }}>${pricingStore.proMonthlyPrice}</span>
                  <span style={{ color: '#4ade80', marginLeft: '8px' }}>FREE</span>
                </>
              );
            }
          })()}
        </div>
        <div style={{ opacity: 0.8 }}>
          {(() => {
            const couponInfo = getCouponDisplayInfo(autoAppliedCoupon);
            if (!couponInfo) return 'per month';
            
            if (couponInfo.type === 'percentage') {
              return `${couponInfo.percentOff}% off your first month, then $9.99/month`;
            } else {
              return 'FREE for 7 days + 1 month, then $9.99/month';
            }
          })()}
        </div>
        {autoAppliedCoupon && (
          <div style={{ 
            fontSize: '12px', 
            color: '#4ade80',
            marginTop: '4px'
          }}>
            {(() => {
              const couponInfo = getCouponDisplayInfo(autoAppliedCoupon);
              if (!couponInfo) return '';
              
              if (couponInfo.type === 'percentage') {
                const discountedYearly = getDiscountedPrice(119.88, couponInfo);
                const discountedMonthly = discountedYearly / 12;
                return (
                  <>
                    <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>Then $119.88/year ($9.99/month)</span>
                    <br />
                    <span>Then ${discountedYearly.toFixed(2)}/year ($${discountedMonthly.toFixed(2)}/month)</span>
                  </>
                );
              } else {
                return 'Then $119.88/year ($9.99/month)';
              }
            })()}
          </div>
        )}
        {autoAppliedCoupon && (
          <div style={{ 
            fontSize: '11px', 
            color: '#9ca3af',
            marginTop: '2px'
          }}>
            {(() => {
              const couponInfo = getCouponDisplayInfo(autoAppliedCoupon);
              if (!couponInfo) return '';
              
              if (couponInfo.type === 'percentage') {
                const discountedMonthly = getDiscountedPrice(9.99, couponInfo);
                const discountedWeekly = discountedMonthly / 4.33;
                return (
                  <>
                    <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>Then $9.99/month ($2.30/week)</span>
                    <br />
                    <span>Then ${discountedMonthly.toFixed(2)}/month ($${discountedWeekly.toFixed(2)}/week)</span>
                  </>
                );
              } else {
                return 'Then $9.99/month ($2.30/week)';
              }
            })()}
          </div>
        )}
        <div style={{ fontSize: '14px', opacity: 0.7, marginTop: '5px' }}>No contract, cancel anytime</div>
        <ul style={{ textAlign: 'left', marginTop: '15px', paddingLeft: '20px', flex: 1 }}>
          <li style={{ color: '#4ade80', fontWeight: 'bold' }}>✓ Convert any link, file, email, text or RSS feed into 100+ human voices instantly</li>
          <li style={{ color: '#4ade80', fontWeight: 'bold' }}>✓ Listen on your podcast player or any browser - No app needed!</li>
          <li>✓ Adjustable playback speed</li>
          <li>✓ 80+ languages support</li>
          <li>✓ Download your audio</li>
          <li>✓ Get AI summaries of your content</li>
          <li>✓ Human support</li>
          <li>📝 Up to 100k chars (~{estimateAudioDuration(100000)} min) per article</li>
        </ul>
      </div>

      {/* Annual Pro Plan Card */}
      <div
        onClick={() => onPlanSelect(pricingStore.proYearlySlug)}
        style={{
          padding: '20px',
          border: '2px solid hsla(0,0%,100%,.1)',
          borderRadius: '8px',
          width: '100%',
          maxWidth: '300px',
          height: '500px',
          cursor: showPaymentElement ? 'not-allowed' : 'pointer',
          backgroundColor: selectedPlan === pricingStore.proYearlySlug ? 'rgba(255,255,255,0.1)' : 'transparent',
          boxSizing: 'border-box',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {autoAppliedCoupon ? (
          <div style={{ 
            position: 'absolute',
            top: '-12px',
            left: '20px',
            backgroundColor: '#4ade80',
            color: 'white',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 'bold'
          }}>
            SPECIAL OFFER
          </div>
        ) : (
          <div style={{ 
            position: 'absolute',
            top: '-12px',
            right: '20px',
            backgroundColor: 'rgb(255 143 65)',
            color: 'white',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 'bold'
          }}>
            BEST VALUE
          </div>
        )}
        <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>Annual Pro</div>
        <div style={{ 
          fontSize: '32px', 
          fontWeight: 'bold', 
          color: 'rgb(255 143 65)',
          marginBottom: '5px'
        }}>
          {(() => {
            const couponInfo = getCouponDisplayInfo(autoAppliedCoupon);
            if (!couponInfo) return `$${(pricingStore.proYearlyPrice / 12).toFixed(2)}`;
            
            if (couponInfo.type === 'percentage') {
              const discountedPrice = getDiscountedPrice(pricingStore.proYearlyPrice / 12, couponInfo);
              return (
                <>
                  <span style={{ textDecoration: 'line-through', opacity: 0.6, fontSize: '24px' }}>${(pricingStore.proYearlyPrice / 12).toFixed(2)}</span>
                  <span style={{ color: '#4ade80', marginLeft: '8px' }}>${discountedPrice.toFixed(2)}</span>
                </>
              );
            } else {
              return (
                <>
                  <span style={{ textDecoration: 'line-through', opacity: 0.6, fontSize: '24px' }}>${(pricingStore.proYearlyPrice / 12).toFixed(2)}</span>
                  <span style={{ color: '#4ade80', marginLeft: '8px' }}>FREE</span>
                </>
              );
            }
          })()}
        </div>
        <div style={{ opacity: 0.8 }}>
          {(() => {
            const couponInfo = getCouponDisplayInfo(autoAppliedCoupon);
            if (!couponInfo) return 'per month (billed annually)';
            
            if (couponInfo.type === 'percentage') {
              return `${couponInfo.percentOff}% off your first year, then $5.83/month (billed annually)`;
            } else {
              return 'FREE for 7 days + 1 month, then $5.83/month (billed annually)';
            }
          })()}
        </div>
        {autoAppliedCoupon && (
          <div style={{ 
            fontSize: '12px', 
            color: '#4ade80',
            marginTop: '4px'
          }}>
            {(() => {
              const couponInfo = getCouponDisplayInfo(autoAppliedCoupon);
              if (!couponInfo) return '';
              
              if (couponInfo.type === 'percentage') {
                const discountedYearly = getDiscountedPrice(69.96, couponInfo);
                const discountedMonthly = discountedYearly / 12;
                return (
                  <>
                    <span style={{ textDecoration: 'line-through', opacity: 0.6 }}>Then $69.96/year ($5.83/month)</span>
                    <br />
                    <span>Then ${discountedYearly.toFixed(2)}/year ($${discountedMonthly.toFixed(2)}/month)</span>
                  </>
                );
              } else {
                return 'Then $69.96/year ($5.83/month)';
              }
            })()}
          </div>
        )}
        <div style={{ 
          fontSize: '14px', 
          color: '#4CAF50', 
          fontWeight: 'bold',
          marginTop: '5px'
        }}>
          Save ${(pricingStore.proMonthlyPrice * 12 - pricingStore.proYearlyPrice).toFixed(2)} ({Math.round(100 - (pricingStore.proYearlyPrice / (pricingStore.proMonthlyPrice * 12)) * 100)}% off)
        </div>
        <div style={{ fontSize: '14px', opacity: 0.7, marginTop: '5px' }}>No contract, cancel anytime</div>
        <ul style={{ textAlign: 'left', marginTop: '15px', paddingLeft: '20px', flex: 1 }}>
          <li style={{ color: '#4ade80', fontWeight: 'bold' }}>✓ Convert any link, file, email, text or RSS feed into 100+ human voices instantly</li>
          <li style={{ color: '#4ade80', fontWeight: 'bold' }}>✓ Listen on your podcast player or any browser - No app needed!</li>
          <li>✓ Adjustable playback speed</li>
          <li>✓ 80+ languages support</li>
          <li>✓ Download your audio</li>
          <li>✓ Get AI summaries of your content</li>
          <li>✓ Human support</li>
          <li>📝 Up to 100k chars (~{estimateAudioDuration(100000)} min) per article</li>
        </ul>
      </div>
    </>
  );

  const renderAllPlansCards = () => (
    <>
      {/* Basic Plan Card */}
      <div
        onClick={() => onPlanSelect(isYearly ? 'basic-yearly' : 'basic-monthly')}
        style={{
          padding: '20px',
          border: '2px solid hsla(0,0%,100%,.1)',
          borderRadius: '8px',
          width: '100%',
          maxWidth: '300px',
          cursor: showPaymentElement ? 'not-allowed' : 'pointer',
          backgroundColor: selectedPlan.includes('basic') ? 'rgba(255,255,255,0.1)' : 'transparent',
          boxSizing: 'border-box'
        }}
      >
        <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>Audioread Basic</div>
        <div>
          {isYearly ? (
            <>
              <div style={{ marginBottom: '5px' }}>
                <span style={{ 
                  fontSize: '16px', 
                  textDecoration: 'line-through', 
                  opacity: 0.7,
                  marginRight: '8px'
                }}>
                  ${(pricingStore.basicMonthlyPrice * 12).toFixed(2)}
                </span>
                <span style={{ 
                  fontSize: '32px', 
                  fontWeight: 'bold', 
                  color: 'rgb(255 143 65)'
                }}>
                  ${pricingStore.basicYearlyPrice}
                </span>
              </div>
              <div style={{ opacity: 0.8 }}>per year</div>
              <div style={{ 
                fontSize: '14px', 
                color: '#4CAF50', 
                fontWeight: 'bold',
                marginTop: '5px'
              }}>
                Save ${(pricingStore.basicMonthlyPrice * 12 - pricingStore.basicYearlyPrice).toFixed(2)} (${Math.round((1 - pricingStore.basicYearlyPrice / (pricingStore.basicMonthlyPrice * 12)) * 100)}% off)
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'rgb(255 143 65)' }}>
                ${pricingStore.basicMonthlyPrice}
              </div>
              <div style={{ opacity: 0.8 }}>per month</div>
            </>
          )}
        </div>
        <ul style={{ textAlign: 'left', marginTop: '15px', paddingLeft: '20px' }}>
          <li>✨ Remove all ads</li>
          <li>🎧 Listen on your podcast player</li>
          <li>📚 Add urls, pdfs, emails, feeds+</li>
          <li>🗣️ Life-like voices</li>
          <li>♾️ Up to ~{estimateAudioDuration(1000000)} minutes of articles</li>
          <li>📱 Unlimited Feeds</li>
          <li style={{ color: '#4CAF50', fontWeight: 'bold' }}>📝 Up to 100k chars (~{estimateAudioDuration(100000)} min) per article</li>
        </ul>
      </div>

      {/* Pro Plan Card */}
      <div
        onClick={() => onPlanSelect(isYearly ? pricingStore.proYearlySlug : pricingStore.proMonthlySlug)}
        style={{
          padding: '20px',
          border: '2px solid hsla(0,0%,100%,.1)',
          borderRadius: '8px',
          width: '100%',
          maxWidth: '300px',
          cursor: showPaymentElement ? 'not-allowed' : 'pointer',
          backgroundColor: selectedPlan.includes('pro') ? 'rgba(255,255,255,0.1)' : 'transparent',
          boxSizing: 'border-box',
          position: 'relative'
        }}
      >
        <div style={{ 
          position: 'absolute',
          top: '-12px',
          right: '20px',
          backgroundColor: 'rgb(255 143 65)',
          color: 'white',
          padding: '4px 12px',
          borderRadius: '12px',
          fontSize: '12px',
          fontWeight: 'bold'
        }}>
          {pricingStore.isHighIncome ? 'PREMIUM' : 'MOST POPULAR'}
        </div>
        <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '10px' }}>
          {pricingStore.isHighIncome ? 'Premium Pro' : 'Audioread Pro'}
        </div>
        <div>
          {isYearly ? (
            <>
              <div style={{ marginBottom: '5px' }}>
                <span style={{ 
                  fontSize: '16px', 
                  textDecoration: 'line-through', 
                  opacity: 0.7,
                  marginRight: '8px'
                }}>
                  ${(pricingStore.proMonthlyPrice * 12).toFixed(2)}
                </span>
                <span style={{ 
                  fontSize: '32px', 
                  fontWeight: 'bold', 
                  color: 'rgb(255 143 65)'
                }}>
                  ${pricingStore.proYearlyPrice}
                </span>
              </div>
              <div style={{ opacity: 0.8 }}>per year</div>
              <div style={{ 
                fontSize: '14px', 
                color: '#4CAF50', 
                fontWeight: 'bold',
                marginTop: '5px'
              }}>
                Save ${(pricingStore.proMonthlyPrice * 12 - pricingStore.proYearlyPrice).toFixed(2)} (${Math.round((1 - pricingStore.proYearlyPrice / (pricingStore.proMonthlyPrice * 12)) * 100)}% off)
              </div>
            </>
          ) : (
            <>
              <div style={{ fontSize: '32px', fontWeight: 'bold', color: 'rgb(255 143 65)' }}>
                ${pricingStore.proMonthlyPrice}
              </div>
              <div style={{ opacity: 0.8 }}>per month</div>
            </>
          )}
        </div>
        <ul style={{ textAlign: 'left', marginTop: '15px', paddingLeft: '20px' }}>
          <li>✨ Everything in Basic</li>
          <li>🎙️ Premium voices</li>
          <li>🌍 Multi-language support</li>
          <li>🤖 AI Summaries</li>
          <li>🔗 Add Feeds by URL</li>
          <li>💾 Download articles as MP3</li>
          <li style={{ color: '#4CAF50', fontWeight: 'bold' }}>📝 Up to 100k chars (~{estimateAudioDuration(100000)} min) per article</li>
        </ul>
      </div>
    </>
  );

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', maxWidth: '100vw', width: '100%', justifyContent: 'center', padding: '0 20px' }}>
      <div
        className="pricing-card"
        style={{
          marginTop: '15px',
          width: '100%',
          maxWidth: '800px',
          border: "2px solid hsla(0,0%,100%,.1)",
          padding: '20px',
          minHeight: 'fit-content',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {!pricingStore.showOnlyPro && !pricingStore.isHighIncome && renderToggle()}
        
        <div style={{ 
          display: 'flex', 
          flexDirection: window.innerWidth < 768 ? 'column' : 'row',
          justifyContent: 'center', 
          alignItems: 'center',
          gap: '20px', 
          margin: '20px 0',
          width: '100%',
          maxWidth: '100%',
          marginLeft: 'auto',
          marginRight: 'auto',
          opacity: showPaymentElement ? 0.5 : 1,
          pointerEvents: showPaymentElement ? 'none' : 'auto',
          flexWrap: 'wrap',
          boxSizing: 'border-box',
          padding: '0 20px'
        }}>
          {pricingStore.isHighIncome ? renderProOnlyCards() : (pricingStore.showOnlyPro ? renderProOnlyCards() : renderAllPlansCards())}
        </div>

        {renderCta && (
          <div style={{ textAlign: 'center', marginTop: '20px', marginBottom: '20px' }}>
            {renderCta('pricing')}
          </div>
        )}
      </div>
    </div>
  );
});

export default PricingCards; 